/* eslint-disable  @typescript-eslint/no-explicit-any */
import { AggregatedTelemetryItem } from '@api/index';
import { ICommand, IEnum, IProperty, ITelemetry, ITelemetryViewState, NumericTelemetryType, TelemetryType } from '../../components/telemetry/models/capability-model-data';
import { CapabilityModelSchemaDataType } from '../../enumerations/capability-model-schema-data-type';
import { CapabilityModelSchemaType } from '../../enumerations/capability-model-schema-type';
import { CapabilityModelType } from '../../enumerations/capability-model-type';
import { IPropertyComment } from '../../interfaces/PropertyComment.interface';
import { ICapabilityModelDefinition } from '../../interfaces/capability-model-definition';
import { IDeviceAnalyticsParameter } from '../../interfaces/device-analytics-parameter';
import { toFixedNumber } from '../number.extensions';
import { tryParseJSON } from '../string.extensions';

type CapabilityModelProperty = {
    name: string;
    displayName: {
        en: string;
    };
    comment: string;
    unit: string;
    displayUnit: {
        en: string;
    };
    schema: {
        '@type': string;
    };
};

export function getTelemetry(capabilityModelDefinition: ICapabilityModelDefinition): ITelemetry[] {
    const parsedCapabilityModel = parseCapabilityModel(capabilityModelDefinition?.model);
    if (!parsedCapabilityModel?.contents) {
        return [];
    }

    const telemetry = parsedCapabilityModel.contents
        .filter((element: { [x: string]: string }) => element['@type'] === 'Telemetry')
        .map((telemetry: { name: string; displayName: { en: string }; comment: string; unit: string; displayUnit: { en: string }; schema: { '@type': string } }) => {
            const customerDefinedName = capabilityModelDefinition?.customerDefinitions?.find((customerDefinition) => customerDefinition?.key === telemetry.name)?.value;

            const parsedTelemetry = {
                id: telemetry.name,
                name: customerDefinedName ?? telemetry.displayName?.en ?? telemetry.name,
                type: tryParseJSON(telemetry.comment)?.type ?? TelemetryType.Output,
                dataType: getCapabilityModelSchemaDataType(telemetry.schema),
                unit: telemetry.unit,
                displayUnit: telemetry.displayUnit?.en,
                dcmUnit: telemetry.unit,
                viewState: ITelemetryViewState.Hidden,
                gradient: tryParseJSON(telemetry.comment)?.gradient,
                offset: tryParseJSON(telemetry.comment)?.offset,
                capabilityModelSchemaType: getCapabilityModelSchemaType(telemetry as any, parsedCapabilityModel.capabilityModelType),
                capabilityModelSchemaDisplayName: getCapabilityModelSchemaDisplayName(telemetry as any, parsedCapabilityModel.capabilityModelType),
                enumValues: getEnumValues((telemetry as any)?.['schema']?.['enumValues']),
            } as ITelemetry;

            return parsedTelemetry;
        });

    return (telemetry ?? []) as ITelemetry[];
}

export function getDeviceAnalyticsParameters(capabilityModelDefinition: ICapabilityModelDefinition): IDeviceAnalyticsParameter[] {
    let parsedCapabilityModel = parseCapabilityModel(capabilityModelDefinition?.model);

    const analyticsParameters = parsedCapabilityModel?.contents?.map(
        (parameter: {
            '@id': string;
            name: string;
            unit: string;
            displayName: { en: string };
            displayUnit: { en: string };
            comment: string;
            schema: { '@type': string; enumValues: [enumValue: string, comment: string, displayName: { en: string }] };
            '@type': string;
        }) =>
            ({
                id: parameter['@id'],
                name: parameter.displayName.en,
                parameterTypeDisplayName:
                    parameter['@type'] === 'Telemetry' && parameter.schema['@type'] === 'Enum' ? CapabilityModelSchemaType[CapabilityModelSchemaType.State] : CapabilityModelSchemaType[CapabilityModelSchemaType.Telemetry],
                parameterTypeId: parameter['@type'] === 'Telemetry' && parameter.schema['@type'] === 'Enum' ? CapabilityModelSchemaType.State : CapabilityModelSchemaType[(parameter as any)['@type']],
                dataTypeDisplayName: CapabilityModelSchemaDataType[getCapabilityModelSchemaDataType(parameter.schema)],
                dataTypeId: getCapabilityModelSchemaDataType(parameter.schema),
                unit: parameter.unit,
                displayUnitName: parameter.displayUnit?.en,
                telemetryType: tryParseJSON(parameter.comment)?.type === TelemetryType.Input ? NumericTelemetryType.Input : NumericTelemetryType.Output,
                initialValue: tryParseJSON(parameter.comment)?.defaultValue,
                isRemanent: tryParseJSON(parameter.comment)?.isRemanent ?? false,
                enumValues: parameter.schema?.enumValues?.map((entity: any) => ({ id: entity.enumValue, name: entity.displayName?.en, color: tryParseJSON(entity.comment)?.color ?? null })),
                capabilityModelSchemaType: getCapabilityModelSchemaType(parameter, parsedCapabilityModel.capabilityModelType),
                capabilityModelSchemaDisplayName: getCapabilityModelSchemaDisplayName(parameter, parsedCapabilityModel.capabilityModelType),
            }) as IDeviceAnalyticsParameter
    );

    return (analyticsParameters ?? []) as IDeviceAnalyticsParameter[];
}

export function getCommands(capabilityModelDefinition: ICapabilityModelDefinition): ICommand[] {
    let parsedCapabilityModel = parseCapabilityModel(capabilityModelDefinition?.model);

    const commands = parsedCapabilityModel?.contents?.filter((element: { [x: string]: string }) => element['@type'] === 'Command');

    return commands;
}

export function getProperties(capabilityModelDefinition: ICapabilityModelDefinition): IProperty[] {
    let parsedCapabilityModel = parseCapabilityModel(capabilityModelDefinition?.model);

    const capabilityModelProperties = parsedCapabilityModel?.contents?.filter((element: { [x: string]: string }) => element['@type'] === 'Property') as CapabilityModelProperty[];

    const properties = getPropertiesInternal(capabilityModelProperties).sort((a, b) => a.name.localeCompare(b.name));
    return properties;
}

export function getGradientValueFixed(property: IPropertyComment, value: number): number;
export function getGradientValueFixed(gradient: number, offset: number, value: number): number;
export function getGradientValueFixed(propertyOrGradient: number | IPropertyComment, offsetOrValue: number, value?: number) {
    let result;
    if (typeof propertyOrGradient === 'object') {
        result = getGradientValue(propertyOrGradient as IPropertyComment, offsetOrValue);
    } else {
        result = getGradientValue(propertyOrGradient as number, offsetOrValue, value);
    }

    return toFixedNumber(result, 2);
}

export function getGradientValue(property: IPropertyComment, value: number): number;
export function getGradientValue(gradient: number, offset: number, value: number): number;
export function getGradientValue(propertyOrGradient: number | IPropertyComment, offsetOrValue: number, value?: number) {
    let gradient, offset;

    if (typeof propertyOrGradient === 'object') {
        gradient = propertyOrGradient.gradient;
        offset = propertyOrGradient.offset;
        value = offsetOrValue;
    } else {
        gradient = propertyOrGradient;
        offset = offsetOrValue;
    }

    if (value == null) {
        return null;
    }

    if (gradient != null) {
        value = Number(value) * gradient;
    }

    if (offset != null) {
        value += offset;
    }

    return value;
}

export function getRawValue(property: IPropertyComment, value: number) {
    if (value == null) {
        return null;
    }

    if (property.offset != null) {
        value -= property.offset;
    }

    if (property.gradient != null) {
        value = Number(value) / property.gradient;
    }
    return value;
}

export function calculateTelemetryGradient(telemetry: ITelemetry, telemetryValue: AggregatedTelemetryItem) {
    const result = {
        timestamp: telemetryValue.timestamp,
        lowerBound: telemetryValue.lowerBound,
        upperBound: telemetryValue.upperBound,
        min: getGradientValue(telemetry, telemetryValue.min),
        average: getGradientValue(telemetry, telemetryValue.average),
        max: getGradientValue(telemetry, telemetryValue.max),
    } as AggregatedTelemetryItem;

    return result;
}

function parseCapabilityModel(capabilityModel: any): { capabilityModelType: CapabilityModelType; contents: any[] } {
    let parsedCapabilityModel = capabilityModel;

    if (capabilityModel && typeof capabilityModel === 'string') {
        parsedCapabilityModel = tryParseJSON(capabilityModel);
    }

    const capabilityModelType = parsedCapabilityModel?.['@id'].includes(CapabilityModelType.IOLink) ? CapabilityModelType.IOLink : CapabilityModelType.Analytics;
    return { capabilityModelType, contents: parsedCapabilityModel?.implements?.[0]?.schema?.contents };
}

export function getCapabilityModelSchemaDataType(schema: { '@type': string; '@valueSchema'?: { '@type': string } }): CapabilityModelSchemaDataType {
    if (schema == null) {
        return CapabilityModelSchemaDataType.Unknown;
    }

    const value = schema?.['@type'];
    if (value?.toLowerCase() === 'enum') {
        return getCapabilityModelSchemaDataType(schema?.['@valueSchema']);
    }

    switch (value) {
        case 'integer':
        case 'long':
        case 'float':
        case 'double':
            return CapabilityModelSchemaDataType.Number;
        case 'string':
            return CapabilityModelSchemaDataType.Text;
        case 'boolean':
            return CapabilityModelSchemaDataType.Boolean;
        default:
            return CapabilityModelSchemaDataType.Unknown;
    }
}

function getEnumValues(enumValues: { enumValue: string; comment: string; displayName: { en: string } }[]) {
    if (enumValues == null) {
        return null;
    }

    return enumValues.reduce((a, x) => ({ ...a, [x.enumValue]: { title: x.displayName.en, properties: tryParseJSON(x.comment) } as IEnum }), {}) as Record<string, IEnum>;
}

function getPropertiesInternal(capabilityModelProperties: CapabilityModelProperty[], schemaKey = '@schema', parentId?: string, parentName?: string) {
    let properties: IProperty[] = [];

    for (const capabilityModelProperty of capabilityModelProperties) {
        const id = capabilityModelProperty.name;
        let name = capabilityModelProperty.displayName?.en ?? capabilityModelProperty.name;

        if (parentName != null) {
            name = `${parentName}: ${name}`;
        }

        const type = (capabilityModelProperty as any)[schemaKey]['@type'];
        if (type.toLowerCase() === 'object') {
            const complexProperties = getPropertiesInternal((capabilityModelProperty as any)[schemaKey].fields, 'schema', id, name);
            properties = [...properties, ...complexProperties];
            continue;
        } else if (type.toLowerCase() === 'array') {
            const arrayItems = getPropertiesArrayInternal(capabilityModelProperty, schemaKey, id, name);
            properties = [...properties, ...arrayItems];
            continue;
        }

        const property = {
            id: capabilityModelProperty.name,
            name,
            dataType: getCapabilityModelSchemaDataType((capabilityModelProperty as any)?.[schemaKey]),
            unit: capabilityModelProperty.unit,
            displayUnit: capabilityModelProperty.displayUnit?.en,
            gradient: tryParseJSON(capabilityModelProperty.comment)?.gradient,
            offset: tryParseJSON(capabilityModelProperty.comment)?.offset,
            enumValues: getEnumValues((capabilityModelProperty as any)?.[schemaKey]?.['enumValues']),
            isComplex: parentId != null,
            parentId,
        } as IProperty;

        properties.push(property);
    }

    return properties;
}

function getPropertiesArrayInternal(capabilityModelProperty: CapabilityModelProperty, schemaKey: string, id: string, name: string) {
    const arrayItems = [];
    const count = tryParseJSON(capabilityModelProperty.comment)?.count ?? 1;
    for (let i = 1; i <= count; i++) {
        const arrayCapabilityModelProperty = {
            ...capabilityModelProperty,
            name: `${id}_${i}`,
            displayName: { en: `${name} ${i}` },
            '@schema': { '@type': (capabilityModelProperty as any)[schemaKey].elementSchema['@type'] },
        };
        const arrayProperty = getPropertiesInternal([arrayCapabilityModelProperty], schemaKey, id, null); // ignore parent name because we set it manually
        arrayItems.push(arrayProperty[0]);
    }
    return arrayItems;
}

function getCapabilityModelSchemaDisplayName(
    telemetry: { '@type': string; name: string; displayName: { en: string }; comment: string; unit: string; displayUnit: { en: string }; schema: { '@type': string } },
    capabilityModelType: CapabilityModelType
) {
    return telemetry['@type'] === 'Telemetry' && telemetry['schema']['@type'] === 'Enum' && capabilityModelType === CapabilityModelType.Analytics
        ? CapabilityModelSchemaType[CapabilityModelSchemaType.State]
        : CapabilityModelSchemaType[CapabilityModelSchemaType.Telemetry];
}

function getCapabilityModelSchemaType(
    telemetry: { '@type': string; name: string; displayName: { en: string }; comment: string; unit: string; displayUnit: { en: string }; schema: { '@type': string } },
    capabilityModelType: CapabilityModelType
) {
    const schemaType = telemetry['@type'] === 'Telemetry' && telemetry['schema']['@type'] === 'Enum' && capabilityModelType === CapabilityModelType.Analytics ? CapabilityModelSchemaType.State : CapabilityModelSchemaType.Telemetry;
    return schemaType;
}
